import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useRouter, useRoute } from 'vue-router';

import { clientsService } from '../utils/api/clientsService';
import { downloadJson } from '../utils/downloadJson';
import { generateUniqueId } from '../utils/uuid';
import type { ClientProps, Client } from '../utils/types';

import { useNetworkStatus } from '../composables/useNetworkStatus';
import { useCurrentClient } from '../composables/useCurrentClient';

import { useClientsStore } from '../store/clients';
import { useUserStore } from '../store/user';

export function useClientSaver() {
  const isPending = ref(false);

  const { isOnline } = useNetworkStatus();
  const { currentClient } = useCurrentClient();

  const toast = useToast();
  const router = useRouter();
  const route = useRoute();

  const clientStore = useClientsStore();
  const userStore = useUserStore();

  function validateUser() {
    if (!userStore.user?.id) {
      throw new Error('User niezalogowany');
    }
  }

  async function addClientHandler(newClient: Client) {
    const client = await clientsService.saveClient(newClient);
    clientStore.addClient(client);
    toast.add({
      severity: 'success',
      summary: 'Dodano klienta',
      life: 3000,
    });
  }

  async function editClientHandler(editedClient: Client) {
    const client = await clientsService.saveClient(editedClient);
    clientStore.editClient(client);
    toast.add({
      severity: 'success',
      summary: 'Dane klienta zostały zmienione',
      life: 3000,
    });
  }

  async function saveNewClient(payload: ClientProps) {
    const newClient = {
      ...payload,
      id: null,
      owner_user_id: Number(userStore.user?.id),
      uuid: generateUniqueId(),
    };

    if (isOnline.value) {
      await addClientHandler(newClient);
    } else {
      clientStore.addClient({ ...newClient, outdated: true });

      await downloadJson(newClient, `client_${newClient.last_name}_${newClient.uuid}`);

      toast.add({
        severity: 'warn',
        summary: 'Dodano klienta w trybie offline',
        life: 10000,
      });
    }

    router.push({
      name: 'ClientView',
      params: {
        clientId: newClient.uuid,
        calculatorId: route.params.calculatorId,
      },
    });
  }

  async function editExistingClient(payload: ClientProps) {
    if (!currentClient.value) {
      throw new Error('Brak klienta do edycji');
    }

    const editedClient = {
      ...currentClient.value,
      ...payload,
    };

    if (isOnline.value) {
      await editClientHandler(editedClient);
    } else {
      clientStore.editClient({ ...editedClient, outdated: true });
      await downloadJson(editedClient, `client_${payload.last_name}_${currentClient.value.uuid}`);
      toast.add({
        severity: 'warn',
        summary: 'Zmieniono klienta w trybie offline',
        life: 10000,
      });
    }
  }

  async function saveClient(payload: ClientProps) {
    isPending.value = true;

    try {
      validateUser();

      if (route.name === 'NewClientView') {
        await saveNewClient(payload);
      } else {
        await editExistingClient(payload);
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Wystąpił błąd podczas zapisywania klienta',
        life: 3000,
      });
    } finally {
      isPending.value = false;
    }
  }

  return {
    saveClient,
    isPending,
    addClientHandler,
    editClientHandler,
  };
}
